<template>

    <div class="card p-3 mb-5">
        <div class="d-flex align-items-center justify-content-start gap-3 px-3">
            <button @click="onBack" class="btn btn-back p-0">
                <span class="svg-icon svg-icon-2">
                    <inline-svg src="/media/icons/duotune/arrows/arr021.svg"/>
                </span>
                <span>{{ $t('common.back') }}</span>
            </button>
            <div>|</div>
            <h2 class="fs-2 fw-bold my-2">
                <template v-if="companyID">
                    {{ sprintf($t('pages.company.editCompanyPattern'), [company.name ?? '']) }}
                </template>
                <template v-else>{{ $t('menu.companyAdd') }}</template>
            </h2>
        </div>
    </div>

    <el-tabs v-model="tabModel" class="user-tabs">
        <el-tab-pane :label="$t('pages.company.save.tabs.generalDetails')" name="general">
            <el-form :model="form.data" ref="userForm">
                <div class="row g-6 mb-6 g-xl-9 mb-xl-9">
                    <div class="col-md-8">
                        <div class="card">
                            <div class="card-body d-flex flex-column p-9">
                                <div class="row">
                                    <div class="col-md-12 fv-row mb-1">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.company.cols.name') }}</label>
                                        <el-form-item prop="name" :rules="$validation.getMessage(['required'])">
                                            <el-input v-model="form.data.name" type="text"/>
                                        </el-form-item>
                                    </div>
                                    <div class="col-md-6 fv-row mb-1">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.company.cols.tax_office') }}</label>
                                        <el-form-item prop="tax_office" :rules="$validation.getMessage(['required'])">
                                            <el-input v-model="form.data.tax_office" type="text"/>
                                        </el-form-item>
                                    </div>
                                    <div class="col-md-6 fv-row mb-1">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.company.cols.tax_number') }}</label>
                                        <el-form-item prop="tax_number" :rules="$validation.getMessage(['required'])">
                                            <el-input v-model="form.data.tax_number" type="text"/>
                                        </el-form-item>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="card">
                            <div class="card-body d-flex flex-column p-9">
                                <div class="fv-row mb-1">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.company.cols.company_type') }}</label>
                                    <el-form-item prop="labels">
                                        <el-select v-model="form.data.company_type_id" class="w-100" :placeholder="$t('common.chooseSelect')" filterable>
                                            <el-option v-for="(type, typeIndex) in types" :key="typeIndex" :value="type.id" :label="type.name"></el-option>
                                        </el-select>
                                    </el-form-item>
                                </div>
                                <div class="fv-row mb-1">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.company.cols.specialties') }}</label>
                                    <el-form-item prop="labels">
                                        <el-select v-model="form.data.specialties" class="w-100" :placeholder="$t('common.chooseSelect')" filterable multiple>
                                            <el-option v-for="(item, itemIndex) in specialties" :key="itemIndex" :value="item.id" :label="item.name"></el-option>
                                        </el-select>
                                    </el-form-item>
                                </div>
                                <div class="fv-row mb-1">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.company.cols.active') }}</label>
                                    <el-form-item prop="status">
                                        <el-radio-group v-model="form.data.active">
                                            <el-radio-button :label="true">{{ $t("common.active") }}</el-radio-button>
                                            <el-radio-button :label="false">{{ $t("common.passive") }}</el-radio-button>
                                        </el-radio-group>
                                    </el-form-item>
                                </div>
                                <div class="fv-row mt-5">
                                    <el-form-item class="mb-0">
                                        <button @click.prevent="onSubmit" :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary w-100" type="button">
                                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                            <span v-if="form.loading" class="indicator-progress">
                                                {{ $t("messages.wait") }}
                                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                            </span>
                                        </button>
                                    </el-form-item>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </el-form>
        </el-tab-pane>
        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.company.save.tabs.module')" name="module">
            <Module :companyID="companyID"/>
        </el-tab-pane>
        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.company.save.tabs.officer')" name="officer">
            <Officer :companyID="companyID"/>
        </el-tab-pane>
        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.company.save.tabs.customer')" name="customer">
            <Customer :companyID="companyID"/>
        </el-tab-pane>
        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.company.save.tabs.domain')" name="domain">
            <Domain @refresh="loadUser" :domains="company.domains" :companyID="companyID"/>
        </el-tab-pane>
    </el-tabs>
</template>

<script>
import Module from "@/components/company/module";
import Officer from "@/components/company/officer";
import Customer from "@/components/company/customer";
import Domain from "@/components/company/domain";

export default {
    name: "_id",
    components: {
        Module,
        Officer,
        Customer,
        Domain,
    },
    data() {
        return {
            tabModel: 'general',
            company: {},
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    company_type_id: undefined,
                    name: '',
                    tax_office: '',
                    tax_number: '',
                    active: true,
                    specialties: []
                },
            },
            rules: {}
        }
    },
    computed: {
        companyID() {
            return this.$route.params.id;
        },
        types() {
            return this.$store.state.company.type.table.data;
        },
        specialties() {
            return this.$store.state.company.specialty.table.data;
        },
    },
    created() {
        if (this.companyID && !(this.companyID > 0)) {
            this.$router.push({
                path: "/company"
            });
        }

        this.rules = {
            password: [{validator: this.validatePass, trigger: ['blur']}],
            password_confirmation: [{validator: this.validatePass2, trigger: ['blur', 'change']}],
        }
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("menu.companyManagement"), []);

        this.$store.dispatch('company/type/get', {
            page: {
                pageSize: 9999
            }
        });
        this.$store.dispatch('company/specialty/get', {
            page: {
                pageSize: 9999
            }
        });

        if (this.companyID && this.companyID > 0) {
            this.loadUser();
        }
    },
    watch: {
        companyID(newValue) {
            if (newValue && newValue > 0) {
                this.form.updateStatus = true;
                this.loadUser(newValue);
            } else {
                this.tabModel = 'general';
                this.form.updateStatus = false;
                this.form.data = {
                    company_type_id: undefined,
                    name: '',
                    tax_office: '',
                    tax_number: '',
                    active: true,
                    specialties: []
                };
                this.company = {};
            }
        }
    },
    methods: {
        onBack() {
            this.$router.push({
                path: "/company"
            });
        },
        loadUser(companyID = this.companyID) {
            this.axios.get(this.endpoints['company'] + '/' + companyID).then((response) => {
                let data = response.data.data;
                this.company = data;

                data.specialties = data.specialties.map((item) => {
                    return item.id;
                });

                this.form.updateStatus = true;
                this.form.data = Object.assign(data);

            });
        },
        onSubmit() {
            this.$refs.userForm.validate((valid) => {
                if (valid) {
                    this.form.loading = true;

                    if (this.form.data.id) {
                        this.axios.put(this.endpoints['company'] + '/' + this.form.data.id, this.form.data).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadUser();
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    } else {
                        this.axios.post(this.endpoints['company'], this.form.data).then(response => {
                            this.onResponse(response.data, () => {
                                let companyID = response.data.data.id;
                                this.$router.push({
                                    path: "/company/save/" + companyID
                                });

                                this.loadUser(companyID);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }
                } else {
                    return false;
                }
            });
        },
    }
}
</script>

<style>
.el-date-editor.el-input,
.el-date-editor.el-input__inner {
    width: 100%;
}
</style>